/**
 * Modelos do projeto — o padrão 02: lista com painel à direita.
 *
 * O modelo é o que amarra o projeto ao mundo real, e é dele que depende meia
 * tela do sistema: a RN-01 esconde pinout restrito a outro modelo, o gráfico se
 * gera para um modelo, e publicar um modelo é o que põe o conteúdo no ar.
 *
 * **Publicar não é um campo do formulário.** É ação de linha, com confirmação,
 * porque muda o que o cliente enxerga — misturar isso com "salvar a descrição"
 * é como o legado deixa acontecer engano.
 */

import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import {
  usarExcluirModelo, usarModelosDoProjeto, usarMotores, usarProdutos, usarPublicarModelo,
  usarSalvarModelo, usarVeiculos, type ModeloRequest, type ModeloResponse,
} from "@/api/modelos"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { CampoDeBusca, idDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"

/**
 * A data vem ISO da API; o campo do navegador quer só o dia.
 *
 * **1900 quer dizer vazio.** A coluna no banco é `smalldatetime`, que não aceita
 * nulo e cai no mínimo dela quando o modelo não tem faixa de ano. Mostrar
 * "1900–1900" na lista seria mostrar um dado que ninguém digitou.
 */
const VAZIA = (d: string | null) => !d || d.slice(0, 4) === "1900" || d.slice(0, 4) === "0001"
const soODia = (d: string | null) => (VAZIA(d) ? "" : d!.slice(0, 10))
const anoDe = (d: string | null) => (VAZIA(d) ? "" : d!.slice(0, 4))

export function Modelos() {
  const { projetoId } = useParams()
  const id = Number(projetoId)

  const { data, isPending, error } = usarModelosDoProjeto(id)
  const [selecionado, setSelecionado] = useState<ModeloResponse | null>(null)
  const [incluindo, setIncluindo] = useState(false)

  const salvar = usarSalvarModelo(id)
  const excluir = usarExcluirModelo(id)
  const publicar = usarPublicarModelo(id)

  const modelos = data ?? []
  const aberto = incluindo || selecionado != null

  // A lista muda embaixo (salvou, publicou): reancora no registro atualizado.
  useEffect(() => {
    if (!selecionado) return
    const novo = modelos.find((m) => m.id === selecionado.id)
    if (novo && novo !== selecionado) setSelecionado(novo)
  }, [modelos, selecionado])

  const colunas: Coluna<ModeloResponse>[] = [
    { chave: "descricao", rotulo: "Modelo", minimo: 240, render: (m) => m.descricao },
    { chave: "veiculo", rotulo: "Veículo", largura: 150, render: (m) => m.veiculo },
    { chave: "motor", rotulo: "Motor", largura: 190, render: (m) => m.motor },
    {
      chave: "anos", rotulo: "Anos", largura: 100, mono: true,
      render: (m) => [anoDe(m.anoIni), anoDe(m.anoFim)].filter(Boolean).join("–"),
    },
    { chave: "produto", rotulo: "Produto", largura: 170, render: (m) => m.produtoNome },
    {
      chave: "publicado", rotulo: "No ar", largura: 92,
      render: (m) => (m.publicado
        ? <Selo tom="ok">publicado</Selo>
        : <span style={{ color: "var(--txt3)" }}>—</span>),
    },
  ]

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          height: 40, flex: "0 0 40px", display: "flex", alignItems: "center", gap: 10,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <span style={{ fontSize: 12, color: "var(--txt2)" }}>
          O modelo diz a que veículo este projeto serve — e é por ele que a restrição esconde pinout.
        </span>
        <span style={{ marginLeft: "auto" }} />
        <button
          onClick={() => { setSelecionado(null); setIncluindo(true) }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 11px",
            borderRadius: 7, background: "var(--brand)", color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
            <path d="M12 5.5v13M5.5 12h13" />
          </svg>
          Novo modelo
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Tabela<ModeloResponse>
            colunas={colunas}
            itens={modelos}
            chaveDe={(m) => m.id}
            selecionada={selecionado?.id ?? null}
            aoSelecionar={(m) => { setIncluindo(false); setSelecionado(m) }}
            vazio={
              isPending ? <LinhaDeAviso>Carregando os modelos…</LinhaDeAviso>
              : error ? (
                <LinhaDeAviso>
                  <b style={{ color: "var(--erro)" }}>
                    {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
                  </b>
                </LinhaDeAviso>
              ) : (
                <LinhaDeAviso>
                  Este projeto ainda não tem modelo.
                  <span style={{ fontSize: 12, color: "var(--txt3)", maxWidth: "48ch" }}>
                    Sem modelo o pinout gráfico não tem o que desenhar, e nada vai ao ar.
                  </span>
                </LinhaDeAviso>
              )
            }
          />
        </div>

        {aberto && (
          <PainelDoModelo
            projetoId={id}
            modelo={incluindo ? null : selecionado}
            salvando={salvar.isPending}
            aoSalvar={async (dados) => {
              try {
                const salvo = await salvar.mutateAsync({ id: selecionado?.id ?? null, dados })
                setIncluindo(false)
                setSelecionado(salvo)
                return null
              } catch (e) { return e }
            }}
            aoFechar={() => { setIncluindo(false); setSelecionado(null) }}
            aoExcluir={async () => {
              if (!selecionado) return
              try { await excluir.mutateAsync(selecionado.id); setSelecionado(null) } catch { /* mostrado no painel */ }
            }}
            excluindo={excluir.isPending}
            aoPublicar={async (pub) => {
              if (!selecionado) return
              try { await publicar.mutateAsync({ id: selecionado.id, publicar: pub }) } catch { /* idem */ }
            }}
            publicando={publicar.isPending}
            erroDaPublicacao={publicar.error}
          />
        )}
      </div>

      <BarraDeEstado>
        {modelos.length > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{modelos.length}</b>{" "}
            {modelos.length === 1 ? "modelo" : "modelos"}
            {" · "}
            <b className="num" style={{ color: "var(--txt)" }}>{modelos.filter((m) => m.publicado).length}</b> no ar
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>Enter numa linha abre para editar</span>
      </BarraDeEstado>
    </div>
  )
}

/** O painel de um modelo. Exportado para a restrição poder incluir modelo sem sair da tela. */
export function PainelDoModelo({
  projetoId, modelo, salvando, aoSalvar, aoFechar, aoExcluir, excluindo,
  aoPublicar, publicando, erroDaPublicacao, encaixado,
}: {
  /** Ocupa a área de quem o abriu (o painel da restrição), em vez da coluna própria de 400px. */
  encaixado?: boolean
  projetoId: number
  modelo: ModeloResponse | null
  salvando: boolean
  aoSalvar: (d: ModeloRequest) => Promise<unknown>
  aoFechar: () => void
  aoExcluir: () => void
  excluindo: boolean
  aoPublicar: (publicar: boolean) => void
  publicando: boolean
  erroDaPublicacao: unknown
}) {
  const incluindo = modelo == null
  const veiculos = usarVeiculos()
  const motores = usarMotores()
  const produtos = usarProdutos()

  const [descricao, setDescricao] = useState("")
  const [veiculo, setVeiculo] = useState<ValorBusca>(VAZIO)
  const [motor, setMotor] = useState<ValorBusca>(VAZIO)
  // -1 = ainda não escolheu. NÃO pode ser 0: o produto de id 0 é "Caminhões",
  // e um formulário que abre em 0 cria modelo em Caminhões sem ninguém pedir.
  const [produtoId, setProdutoId] = useState(-1)
  const [anoIni, setAnoIni] = useState("")
  const [anoFim, setAnoFim] = useState("")
  const [erro, setErro] = useState<unknown>(null)
  const [confirmando, setConfirmando] = useState(false)

  // Recarrega quando troca de linha. Veículo e motor entram como `vinculado`:
  // os ids vieram do banco e o rótulo, da própria listagem.
  useEffect(() => {
    setDescricao(modelo?.descricao ?? "")
    setVeiculo(modelo?.veiculoId
      ? { estado: "vinculado", id: modelo.veiculoId, texto: modelo.veiculo } : VAZIO)
    setMotor(modelo?.motorId
      ? { estado: "vinculado", id: modelo.motorId, texto: modelo.motor } : VAZIO)
    setProdutoId(modelo?.produtoId ?? -1)
    setAnoIni(soODia(modelo?.anoIni ?? null))
    setAnoFim(soODia(modelo?.anoFim ?? null))
    setErro(null)
    setConfirmando(false)
  }, [modelo])

  // As duas vêm do campo como AAAA-MM-DD: comparar o texto é comparar a data.
  const faixaInvertida = anoIni !== "" && anoFim !== "" && anoFim < anoIni

  const faltando = !descricao.trim() ? "Informe a descrição."
    : idDe(veiculo) <= 0 ? "Escolha o veículo."
    : idDe(motor) <= 0 ? "Escolha o motor."
    : produtoId < 0 ? "Escolha o produto."
    : faixaInvertida ? "O ano final não pode ser anterior ao ano inicial."
    : null

  async function gravar() {
    if (faltando) { setErro(new Error(faltando)); return }
    const e = await aoSalvar({
      projetoId,
      descricao: descricao.trim(),
      veiculoId: idDe(veiculo),
      motorId: idDe(motor),
      anoIni: anoIni || null,
      anoFim: anoFim || null,
      produtoId,
    })
    setErro(e ?? null)
  }

  return (
    <aside
      className={encaixado ? undefined : "gaveta"}
      style={{
        ...(encaixado
          ? { flex: "1 1 auto", width: "100%", height: "100%" }
          : { flex: "0 0 400px", width: 400, borderLeft: "1px solid var(--line2)" }),
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
      }}
    >
      <form
        onSubmit={(ev) => { ev.preventDefault(); void gravar() }}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); void gravar() }
          if (ev.key === "Escape") { ev.preventDefault(); aoFechar() }
        }}
        style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <div style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
          padding: "0 14px", height: 44, borderBottom: "1px solid var(--line)",
        }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600,
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {incluindo ? "Novo modelo" : modelo!.descricao}
          </span>
          <button type="button" onClick={aoFechar} aria-label="Fechar o painel"
                  style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 11 }}>
          <Rotulo texto="Descrição" obrigatorio>
            <input
              className="campo"
              value={descricao}
              onChange={(ev) => setDescricao(ev.target.value)}
              placeholder="Hyundai i30 1.6 16V AT (High B357) #2013-2014#"
              style={entrada}
            />
          </Rotulo>

          <Rotulo texto="Veículo" obrigatorio>
            <CampoDeBusca
              rotulo="Veículo" valor={veiculo} aoMudar={setVeiculo}
              fonte={{ modo: "memoria", itens: veiculos.itens, carregando: veiculos.carregando }}
              permiteCriar={false} id="modelo-veiculo" dica="digite parte do nome"
            />
          </Rotulo>

          <Rotulo texto="Motor" obrigatorio>
            <CampoDeBusca
              rotulo="Motor" valor={motor} aoMudar={setMotor}
              fonte={{ modo: "memoria", itens: motores.itens, carregando: motores.carregando }}
              permiteCriar={false} id="modelo-motor" dica="digite parte do nome"
            />
          </Rotulo>

          <Rotulo texto="Produto" obrigatorio>
            <select className="campo" value={produtoId} style={entrada}
                    onChange={(ev) => setProdutoId(Number(ev.target.value))}>
              <option value={-1}>Escolha o produto…</option>
              {produtos.itens.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </Rotulo>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Rotulo texto="Ano inicial">
              <input type="date" className="campo" value={anoIni} style={entrada}
                     onChange={(ev) => setAnoIni(ev.target.value)} />
            </Rotulo>
            <Rotulo texto="Ano final">
              <input type="date" className="campo" value={anoFim} min={anoIni || undefined}
                     aria-invalid={faixaInvertida}
                     style={{ ...entrada, ...(faixaInvertida ? { borderColor: "var(--erro)" } : {}) }}
                     onChange={(ev) => setAnoFim(ev.target.value)} />
            </Rotulo>
          </div>
          {faixaInvertida && (
            <span role="alert" style={{ fontSize: 11.5, color: "var(--erro)", marginTop: -4 }}>
              O ano final não pode ser anterior ao ano inicial.
            </span>
          )}

          {!incluindo && (
            <div style={{
              marginTop: 4, padding: "11px 12px", borderRadius: 9,
              background: "var(--surface2)", display: "flex", flexDirection: "column", gap: 8,
            }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
                {modelo!.publicado ? "Este modelo está no ar" : "Este modelo não está no ar"}
              </span>
              <span style={{ fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
                {modelo!.publicado
                  ? "Despublicar tira o conteúdo da vista do cliente e marca o projeto para reprocessar."
                  : "Publicar põe o conteúdo à vista do cliente e marca o projeto para reprocessar."}
              </span>
              <button
                type="button"
                onClick={() => aoPublicar(!modelo!.publicado)}
                disabled={publicando}
                style={{
                  alignSelf: "flex-start", height: 30, padding: "0 12px", borderRadius: 7,
                  fontSize: 12.5, fontWeight: 600,
                  background: modelo!.publicado ? "var(--surface)" : "var(--acao)",
                  color: modelo!.publicado ? "var(--txt)" : "var(--acao-ink)",
                  border: modelo!.publicado ? "1px solid var(--line2)" : undefined,
                  opacity: publicando ? 0.6 : 1,
                }}
              >
                {publicando ? "Aguarde…" : modelo!.publicado ? "Despublicar" : "Publicar"}
              </button>
              {erroDaPublicacao != null && <Erro erro={erroDaPublicacao} />}
            </div>
          )}

          {erro != null && <Erro erro={erro} />}
        </div>

        <div style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
          padding: 12, borderTop: "1px solid var(--line)",
        }}>
          {!incluindo && (
            confirmando ? (
              <button type="button" onClick={aoExcluir} disabled={excluindo}
                      style={{ height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5,
                               fontWeight: 600, background: "var(--erro)", color: "#FFFFFF" }}>
                {excluindo ? "Excluindo…" : "Confirmar exclusão"}
              </button>
            ) : (
              <button type="button" onClick={() => setConfirmando(true)}
                      style={{ height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5,
                               fontWeight: 600, color: "var(--erro)", border: "1px solid var(--line2)" }}>
                Excluir
              </button>
            )
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={aoFechar}
                  style={{ height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5, color: "var(--txt2)" }}>
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 13px",
                    borderRadius: 7, background: "var(--brand)", color: "#FFFFFF",
                    fontSize: 12.5, fontWeight: 600, opacity: salvando ? 0.6 : 1,
                  }}>
            {salvando ? "Salvando…" : incluindo ? "Incluir" : "Salvar"}
            <kbd style={{ fontSize: 10, opacity: 0.8 }}>Ctrl ↵</kbd>
          </button>
        </div>
      </form>
    </aside>
  )
}

const entrada: React.CSSProperties = {
  width: "100%", padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Rotulo({ texto, obrigatorio, children }: {
  texto: string; obrigatorio?: boolean; children: React.ReactNode
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>
        {texto}{obrigatorio && <span style={{ color: "var(--erro)", marginLeft: 3 }}>*</span>}
      </span>
      {children}
    </label>
  )
}

function Selo({ tom, children }: { tom: "ok"; children: React.ReactNode }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", height: 19, padding: "0 8px",
      borderRadius: 999, fontSize: 10.5, fontWeight: 600,
      background: tom === "ok" ? "var(--ok-soft)" : "var(--surface2)",
      color: tom === "ok" ? "var(--ok-on-soft)" : "var(--txt2)",
    }}>
      {children}
    </span>
  )
}

function Erro({ erro }: { erro: unknown }) {
  if (erro instanceof ErroDeTrava) {
    return <Caixa tom="acao" titulo="Bloqueado pela trava do canário">{erro.message}</Caixa>
  }
  if (erro instanceof ErroDaApi && erro.ehConflito) {
    return <Caixa tom="acao" titulo="Não deu para concluir">{erro.message}</Caixa>
  }
  const texto = erro instanceof ErroDaApi ? erro.message
    : erro instanceof Error ? erro.message
    : "Não foi possível salvar."
  return <Caixa tom="erro">{texto}</Caixa>
}

function Caixa({ tom, titulo, children }: {
  tom: "acao" | "erro"; titulo?: string; children: React.ReactNode
}) {
  return (
    <div style={{
      padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
      background: tom === "acao" ? "var(--acao-soft)" : "var(--erro-soft)",
      color: tom === "acao" ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
      border: tom === "acao" ? "1px solid var(--acao)" : undefined,
    }}>
      {titulo && <b style={{ display: "block", marginBottom: 3 }}>{titulo}</b>}
      {children}
    </div>
  )
}
